import { createSlice } from "@reduxjs/toolkit";
import * as secureStorage from "../../utils/secureStorage";

const LOCAL_STORAGE_KEYS = {
  USER_NAME: 'auth_user_name',
  PHONE_NO: 'auth_phone_no',
  PROFILE_STATUS: 'auth_profile_status',
  ENTITY_TYPE: 'auth_entity_type',
  DCS_ID: 'auth_dcs_id',
  CHECKER_ID: 'auth_checker_id',
  APPROVER_ID: 'auth_approver_id',
  UNION_ID: 'auth_union_id',
};

const getInitialAuthState = () => {
  const token = secureStorage.getToken();
  const role = secureStorage.getRole();
  const entityType = secureStorage.getEntityType() || secureStorage.getEntityTypeFromJWT();
  const permissions = secureStorage.getPermissions();

  const userName = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_NAME) ||
    secureStorage.getUserNameFromJWT();
  const phoneNo = localStorage.getItem(LOCAL_STORAGE_KEYS.PHONE_NO) ||
    secureStorage.getPhoneFromJWT();

  const profileStr = localStorage.getItem(LOCAL_STORAGE_KEYS.PROFILE_STATUS);
  let profile = null;
  if (profileStr === 'true') {
    profile = true;
  } else if (profileStr === 'false') {
    profile = false;
  }

  const dcsId = localStorage.getItem(LOCAL_STORAGE_KEYS.DCS_ID);
  const checkerId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHECKER_ID);
  const approverId = localStorage.getItem(LOCAL_STORAGE_KEYS.APPROVER_ID);
  const unionId = localStorage.getItem(LOCAL_STORAGE_KEYS.UNION_ID);

  return {
    token: token || null,
    userName: userName || null,
    role: role || null,
    entityType: entityType || null,
    profile: profile || null,
    permissions: permissions || [],
    dcsId: dcsId || null,
    checkerId: checkerId || null,
    approverId: approverId || null,
    unionId: unionId || null,
    phoneNo: phoneNo || null,
    isAuthenticated: !!token,
    loading: false,
    error: null,
  };
};

const ENTITY_CONFIG = {
  DCS: { stateKey: 'dcsId', storageKey: LOCAL_STORAGE_KEYS.DCS_ID },
  CHECKER: { stateKey: 'checkerId', storageKey: LOCAL_STORAGE_KEYS.CHECKER_ID },
  APPROVER: { stateKey: 'approverId', storageKey: LOCAL_STORAGE_KEYS.APPROVER_ID },
  UNION: { stateKey: 'unionId', storageKey: LOCAL_STORAGE_KEYS.UNION_ID },
};

const resetEntityIds = (state) => {
  state.dcsId = null;
  state.checkerId = null;
  state.approverId = null;
  state.unionId = null;
};

const clearEntityStorage = () => {
  Object.values(ENTITY_CONFIG).forEach(({ storageKey }) => {
    localStorage.removeItem(storageKey);
  });
};

const setEntityIdState = (state, entityType, entityId) => {
  resetEntityIds(state);
  const config = ENTITY_CONFIG[entityType];
  if (config) {
    state[config.stateKey] = entityId || null;
  }
};

const setEntityIdStorage = (entityType, entityId) => {
  clearEntityStorage();
  const config = ENTITY_CONFIG[entityType];
  if (config && entityId) {
    localStorage.setItem(config.storageKey, entityId);
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialAuthState(),
  reducers: {
    loginSuccess: (state, action) => {
      const {
        token,
        refreshToken,
        userName,
        role,
        entityType,
        profile,
        entityId,
        phoneNo,
        permissions = [],
        unionId,
        userData,
      } = action.payload;

      state.token = token;
      state.userName = userName;
      state.role = role;
      state.profile = profile;
      state.permissions = permissions;
      state.phoneNo = phoneNo;
      state.isAuthenticated = true;
      state.error = null;

      const normalizedEntityType = entityType?.toUpperCase() || null;
      state.entityType = normalizedEntityType;

      setEntityIdState(state, normalizedEntityType, entityId);
      if (unionId) {
        state.unionId = unionId;
      }

      secureStorage.setToken(token, 57600);
      secureStorage.setRefreshToken(refreshToken);
      secureStorage.setRole(role);
      secureStorage.setEntityType(normalizedEntityType);
      secureStorage.setPermissions(permissions);
      if (userData) {
        secureStorage.setUserData(userData);
      }

      localStorage.setItem(LOCAL_STORAGE_KEYS.USER_NAME, userName);
      localStorage.setItem(LOCAL_STORAGE_KEYS.PHONE_NO, phoneNo);
      localStorage.setItem(LOCAL_STORAGE_KEYS.PROFILE_STATUS, String(profile));

      setEntityIdStorage(normalizedEntityType, entityId);
      if (unionId) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.UNION_ID, unionId);
      }
    },

    updateToken: (state, action) => {
      const { token, refreshToken } = action.payload;
      state.token = token;
      secureStorage.setToken(token, 57600);
      if (refreshToken) {
        secureStorage.setRefreshToken(refreshToken);
      }
    },

    updateProfileStatus: (state, action) => {
      const newProfileStatus = action.payload;
      state.profile = newProfileStatus;
      localStorage.setItem(LOCAL_STORAGE_KEYS.PROFILE_STATUS, String(newProfileStatus));
    },

    logout: (state) => {
      secureStorage.clearAuth();
      localStorage.clear();
      sessionStorage.clear();
      state.token = null;
      state.userName = null;
      state.role = null;
      state.entityType = null;
      state.profile = null;
      state.permissions = [];
      state.dcsId = null;
      state.checkerId = null;
      state.approverId = null;
      state.unionId = null;
      state.phoneNo = null;
      state.isAuthenticated = false;
      state.error = null;
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    initializeAuth: (state) => {
      const initialState = getInitialAuthState();
      state.token = initialState.token;
      state.userName = initialState.userName;
      state.role = initialState.role;
      state.entityType = initialState.entityType;
      state.profile = initialState.profile;
      state.permissions = initialState.permissions;
      state.dcsId = initialState.dcsId;
      state.checkerId = initialState.checkerId;
      state.approverId = initialState.approverId;
      state.unionId = initialState.unionId;
      state.phoneNo = initialState.phoneNo;
      state.isAuthenticated = initialState.isAuthenticated;
    },
  },
});

export const {
  loginSuccess,
  updateToken,
  logout,
  setLoading,
  setError,
  updateProfileStatus,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
